import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, HStack, VStack, Text, Input, Select, IconButton, Divider,
  useColorModeValue, useToast, Spacer,
} from '@chakra-ui/react';
import { FiRepeat, FiPlus, FiX } from 'react-icons/fi';
import { v4 as uuid } from 'uuid';
import useRecurringProposal from '../../hooks/useRecurringProposal';
import useRecurringRevenueCreate, { RecurringEarnerInput, RecurringLineInput } from '../../hooks/useRecurringRevenueCreate';
import { useRecurringRevenuesByProject } from '../../hooks/useRecurringRevenues';
import { RecurringRevenueStatus } from '../../models/recurringRevenue';
import useUsers from '../../hooks/useUsers';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useRnwyFleet, rnwyHealth, rnwyForProject } from '../../hooks/useRnwyStatus'; // item 861/1124
import { useConfirm } from '../ConfirmProvider';
import RnwySubscriptionEnhancementsNote from '../RnwySubscriptionEnhancementsNote'; // item 1544

// dart (2026-07-14) — RNWY 2.0 #24. At the (final) invoice of an automated RNWY deal, prompt to
// enroll the monthly subscription: the server pre-fills the lines (RNWY-MONTHLY / SWITCHPILOT-
// MONTHLY / HELPDESK-MONTHLY, linked to the locked SKUs) and the earners (the customer's IO-Sales /
// Sales-PO reps at their v3 rate). This is CONFIRM, not assign — the %s are editable here, then it
// creates the subscription (firing the Stripe sub + wiring the monthly accrual).
// dart (2026-07-15, Chad) — a GUIDED, deliberate step: renders ONLY for an admin, ONLY once the
// invoice is a closed FINAL one-time invoice (enabled prop), and enrolling requires an explicit
// confirm dialog before it ever hits Stripe. Still hides when there's no recurring revenue or a
// live subscription already exists.
const money = (n: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RnwyEnrollPanel: React.FC<{
  proposalId?: string;
  enabled?: boolean;
  // item 706 — notify the host (e.g. the Recurring-section enroll modal) after a successful
  // enroll so it can close + refresh the not-enrolled list.
  onEnrolled?: () => void;
}> = ({ proposalId, enabled, onEnrolled }) => {
  const { data: payload } = useRecurringProposal(proposalId);
  const { data: existing } = useRecurringRevenuesByProject(payload?.projectId);
  // item 861 — the linked deployed box's live capacity (shared fleet query)
  // item 1124 — aggregated across the project's processors when it runs more than one
  const { data: rnwyFleet } = useRnwyFleet();
  const rnwyRow = rnwyForProject(rnwyFleet, payload?.projectId);
  const { data: users } = useUsers();
  const create = useRecurringRevenueCreate();
  const toast = useToast();
  const { isAdmin } = useCurrentUser();
  const confirmDialog = useConfirm();

  const [dismissed, setDismissed] = useState(false);
  // item 738 (Chad) — card is OUR opt-in at enrollment, not the client's choice: subscriptions
  // bill by ACH; card is rarely offered, so it's an explicit checkbox here, OFF by default
  // (mirrors the backend AllowCard=false default — Stripe only adds card when we allow it).
  const [allowCard, setAllowCard] = useState(false);
  // item 738 (Chad) — the primary invoice email is EDITABLE here (this is the only prompt —
  // Confirm & enroll fires Stripe immediately), plus an optional CC who gets a copy of every
  // monthly invoice (via the invoice.sent webhook; Stripe can't natively CC).
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  // item 746 (Chad) — the client's PO # is OPTIONAL here (often arrives later): it rides the
  // invoice memo/footer, and can be added/changed after enrollment via Edit subscription.
  const [customerPo, setCustomerPo] = useState('');
  // item 1135 (Chad) — future first-bill date (blank = bill now) + item 883 end (blank = forever)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [earners, setEarners] = useState<RecurringEarnerInput[]>([]);
  // item 731 (Chad) — the monthly lines are pre-filled from the sold proposal but EDITABLE here:
  // change an amount, add a new invoiced line, or remove one before enrolling. The total (and the
  // Stripe subscription) follows the edited lines.
  const [lines, setLines] = useState<RecurringLineInput[]>([]);
  useEffect(() => {
    if (payload) {
      setEarners(payload.earners ?? []);
      setLines(payload.lines ?? []);
      setInvoiceEmail(payload.customerEmail ?? ''); // item 738 — prefill, editable
      setCustomerPo(payload.customerPoNumber ?? ''); // item 766 — the approval-time PO pre-fills
    }
  }, [payload]);

  const border = useColorModeValue('purple.200', 'whiteAlpha.300');
  const bg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const fill = useColorModeValue('blackAlpha.50', 'whiteAlpha.50'); // item 958 — light-mode contrast

  // admins only, and only once the final one-time invoice is closed (enabled)
  if (!payload || dismissed || !enabled || !isAdmin) return null;
  // already enrolled — item 774 follow-up (Chad): PER-PROPOSAL, not per-project. The old
  // project-level check made this panel render EMPTY for every new deal once the project had
  // any live sub (the same regression as the pending list — deals stack on one project now).
  // Legacy subs (no proposalId) still match at project level.
  const alreadyEnrolled = (existing?.index ?? []).some(
    (r) => r.status !== RecurringRevenueStatus.Cancelled && !r.isArchive
      && ((r as any).proposalId ? (r as any).proposalId === proposalId : true),
  );
  if (alreadyEnrolled) return null;

  const nameOf = (uid: string) => users?.index?.find((u) => u.id === uid)?.name ?? 'Unknown user';
  const totalMonthly = lines.reduce((s, l) => s + (Number(l.monthlyAmount) || 0), 0);

  const setLineAmount = (idx: number, value: string) => setLines(
    (prev) => prev.map((l, i) => (i === idx ? { ...l, monthlyAmount: parseFloat(value) || 0 } : l)),
  );
  const setLineDesc = (idx: number, value: string) => setLines(
    (prev) => prev.map((l, i) => (i === idx ? { ...l, description: value } : l)),
  );
  const addLine = () => setLines((prev) => [...prev, {
    clientKey: uuid(), sku: '', brand: '', description: '', monthlyAmount: 0,
  }]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const setPercent = (idx: number, value: string) => {
    const pct = value === '' ? 0 : Number(value);
    setEarners((prev) => prev.map((e, i) => (i === idx ? { ...e, percent: pct } : e)));
  };

  // item 731 (Chad) — the earner is no longer fixed to the customer's assigned reps: pick WHO
  // earns (any user) and add/remove rows. The backend already stores an arbitrary earner list
  // (RecurringCommissionEarner) — this just exposes it. Blank userIds are skipped server-side.
  const setEarnerUser = (idx: number, uid: string) => {
    setEarners((prev) => prev.map((e, i) => (i === idx ? { ...e, userId: uid } : e)));
  };
  const addEarner = () => setEarners((prev) => [...prev, { userId: '', percent: 0 }]);
  const removeEarner = (idx: number) => setEarners((prev) => prev.filter((_, i) => i !== idx));

  const confirm = async () => {
    // dart (2026-07-15, Chad) — an explicit, deliberate confirmation before it EVER hits Stripe.
    const ok = await confirmDialog(
      `This creates a live subscription billing ${money(totalMonthly)}/mo and starts recurring commissions. This cannot be undone from here.`,
      { title: 'Enroll subscription & start billing?', confirmLabel: 'Enroll & start billing', cancelLabel: 'Not yet' },
    );
    if (!ok) return;
    try {
      await create.mutateAsync({
        ...payload,
        earners,
        lines,
        allowCard,
        customerEmail: invoiceEmail.trim() || payload.customerEmail,
        ccEmail: ccEmail.trim() || undefined,
        proposalId, // item 746 — remember the deal on the subscription
        customerPoNumber: customerPo.trim() || undefined,
        startDate: startDate || undefined, // item 1135 — future = Stripe's first invoice that day
        endDate: endDate || undefined, // item 883 — Stripe cancel_at; blank = forever
      });
      toast({
        title: 'Subscription enrolled',
        description: 'The monthly subscription is active and recurring commissions will accrue each month.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      setDismissed(true);
      onEnrolled?.();
    } catch (e: any) {
      toast({
        title: 'Enrollment failed',
        description: e?.response?.data ?? 'The subscription could not be created.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }
  };

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={bg} p={4} mt={4}>
      <HStack mb={1}>
        <FiRepeat />
        <Text fontWeight="semibold">Enroll monthly subscription</Text>
      </HStack>
      <Text fontSize="sm" color={sub} mb={3}>
        This deal carries recurring revenue. Confirm the monthly subscription and its commission
        split — pulled from the customer&apos;s assigned reps. Nothing is charged here; Stripe bills
        the subscription and commissions accrue monthly.
      </Text>
      {/* item 1544 — what rides free with this subscription (Settings → RNWY) */}
      <RnwySubscriptionEnhancementsNote compact />

      {/* item 861 — the linked deployed box's live headroom, right where subs get enrolled */}
      {rnwyRow && rnwyRow.ok && (
        <HStack fontSize="xs" color={sub} mb={3} spacing={2}>
          <Box
            w="7px"
            h="7px"
            borderRadius="full"
            flexShrink={0}
            bg={{ green: 'green.400', amber: 'orange.300', red: 'red.400' }[rnwyHealth(rnwyRow.venueCapacity)]}
          />
          <Text>
            {`Live system${rnwyRow.systems.length > 1 ? 's' : ''} ${rnwyRow.systems.join(' + ')}: ${rnwyRow.venuesRunning} of ${rnwyRow.venueTotalCapacity} venues running · ${rnwyRow.venueCapacity} open`}
          </Text>
        </HStack>
      )}

      <VStack align="stretch" spacing={1} mb={2}>
        {lines.map((l, i) => (
          <HStack key={l.clientKey} fontSize="sm" spacing={2}>
            <Input
              size="xs"
              flex="1"
              placeholder="Line description"
              value={l.description}
              onChange={(ev) => setLineDesc(i, ev.target.value)}
              aria-label="Line description"
            />
            <HStack w="110px" spacing={1}>
              <Text color={sub}>$</Text>
              <Input
                size="xs"
                type="number"
                step="0.01"
                min={0}
                textAlign="right"
                placeholder="/mo"
                value={l.monthlyAmount || ''}
                onChange={(ev) => setLineAmount(i, ev.target.value)}
                aria-label="Monthly amount"
              />
            </HStack>
            <IconButton
              aria-label="Remove line"
              size="xs"
              variant="ghost"
              icon={<FiX />}
              onClick={() => removeLine(i)}
            />
          </HStack>
        ))}
        <Button size="xs" variant="outline" leftIcon={<FiPlus />} alignSelf="flex-start" onClick={addLine}>
          Add monthly line
        </Button>
        <Divider my={1} />
        <HStack fontSize="sm" fontWeight="bold">
          <Text>Total monthly</Text>
          <Spacer />
          <Text>{`${money(totalMonthly)}/mo`}</Text>
        </HStack>
      </VStack>

      {/* item 726/738 (Chad) — who the invoice goes to (EDITABLE — this is the only prompt) + how it's billed */}
      <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={fill} p={3} mb={3} fontSize="xs" color={sub}>
        <HStack spacing={2} mb={1.5}>
          <Text w="110px" flexShrink={0} fontWeight="semibold">Invoice email</Text>
          <Input
            size="xs"
            type="email"
            value={invoiceEmail}
            placeholder="billing contact email (required)"
            onChange={(e) => setInvoiceEmail(e.target.value)}
            aria-label="Invoice email"
          />
        </HStack>
        <HStack spacing={2} mb={1.5}>
          <Text w="110px" flexShrink={0} fontWeight="semibold">CC (optional)</Text>
          <Input
            size="xs"
            type="email"
            value={ccEmail}
            placeholder="also send each monthly invoice to…"
            onChange={(e) => setCcEmail(e.target.value)}
            aria-label="CC email"
          />
        </HStack>
        {/* item 746 (Chad) — optional client PO: prints in every invoice's memo/footer; can be
            added later via Edit subscription if it isn't in hand yet */}
        <HStack spacing={2} mb={2}>
          <Text w="110px" flexShrink={0} fontWeight="semibold">Client PO (optional)</Text>
          <Input
            size="xs"
            value={customerPo}
            placeholder="client PO # — printed on every invoice; can be added later"
            onChange={(e) => setCustomerPo(e.target.value)}
            aria-label="Client PO number"
          />
        </HStack>
        {/* item 1135 (Chad) — first-bill date: today = invoice now; a future date = Stripe
            fires the FIRST invoice that day (and anchors every cycle after). item 883 end. */}
        <HStack spacing={2} mb={1.5}>
          <Text w="110px" flexShrink={0} fontWeight="semibold">Start billing</Text>
          <Input
            size="xs"
            type="date"
            value={startDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Start billing date"
          />
          <Text flexShrink={0} opacity={0.7}>{startDate ? 'first invoice on this day' : 'today — first invoice now'}</Text>
        </HStack>
        <HStack spacing={2} mb={2}>
          <Text w="110px" flexShrink={0} fontWeight="semibold">End (optional)</Text>
          <Input
            size="xs"
            type="date"
            value={endDate}
            min={startDate || new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="End date"
          />
          <Text flexShrink={0} opacity={0.7}>{endDate ? 'Stripe stops it this day' : 'blank — runs forever'}</Text>
        </HStack>
        <Text>
          Stripe emails the monthly invoice to the address above, payable by
          {' '}
          <strong>{allowCard ? 'ACH or card' : 'ACH'}</strong>
          . The client can opt into
          {' '}
          <strong>auto-pay</strong>
          {' '}
          after adding a bank account (row menu → Auto-pay). Nothing is charged here.
        </Text>
        {/* item 738 (Chad) — card is OUR opt-in at enrollment (rarely offered), never the client's pick */}
        <Checkbox size="sm" mt={2} isChecked={allowCard} onChange={(e) => setAllowCard(e.target.checked)}>
          <Text fontSize="xs">Also allow card payments (rare — we opt in, not the client)</Text>
        </Checkbox>
      </Box>

      <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color={sub} mb={1}>
        Recurring commission
      </Text>
      <VStack align="stretch" spacing={1} mb={2}>
        {earners.length === 0 && (
          <Text fontSize="sm" color={sub}>
            No recurring commission set — pre-filled from the customer&apos;s assigned reps when they
            have a rate. Add anyone who should earn, or enroll with none.
          </Text>
        )}
        {earners.map((e, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <HStack key={`${e.userId}-${i}`} fontSize="sm" spacing={2}>
            <Select
              size="xs"
              flex="1"
              placeholder="— Person —"
              value={e.userId}
              onChange={(ev) => setEarnerUser(i, ev.target.value)}
              aria-label="Commission earner"
            >
              {(users?.index ?? []).map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
            </Select>
            <Input
              size="xs"
              type="number"
              step="0.01"
              min={0}
              w="80px"
              textAlign="right"
              value={e.percent || ''}
              onChange={(ev) => setPercent(i, ev.target.value)}
              aria-label={`${nameOf(e.userId)} percent`}
            />
            <Text w="14px">%</Text>
            <IconButton
              aria-label="Remove earner"
              size="xs"
              variant="ghost"
              icon={<FiX />}
              onClick={() => removeEarner(i)}
            />
          </HStack>
        ))}
      </VStack>
      <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={addEarner} mb={3}>
        Add earner
      </Button>

      <HStack>
        {/* dart (2026-07-15, Chad) — MUST be type="button". This panel renders INSIDE the invoice
            <form>, and a typeless Chakra Button defaults to type="submit". As the first submit button
            in the form, pressing Enter in any invoice field implicitly clicked it → silently enrolled
            + fired a live Stripe subscription with no confirmation. type="button" stops that. */}
        {/* item 738 — Stripe's send_invoice billing requires an email; block a blank one here */}
        <Button
          type="button"
          size="sm"
          colorScheme="purple"
          isLoading={create.isLoading}
          isDisabled={!invoiceEmail.trim().includes('@')}
          title={!invoiceEmail.trim().includes('@') ? 'Set the invoice email first' : undefined}
          onClick={confirm}
        >
          Confirm &amp; enroll
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setDismissed(true)}>
          Not now
        </Button>
      </HStack>
    </Box>
  );
};

RnwyEnrollPanel.defaultProps = { proposalId: undefined, enabled: false, onEnrolled: undefined };

export default RnwyEnrollPanel;
